'use client';

import { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useLocale } from '@ae/shared';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

require('dayjs/locale/fr');
require(`dayjs/locale/nl`);
require(`dayjs/locale/en`);
require(`dayjs/locale/de`);

dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export const useDate = () => {
  const locale = useLocale();

  const formatDate = useCallback(
    (date: string | Date, separator: string = '.'): string =>
      dayjs(date)
        .locale(locale)
        .tz('Europe/Brussels')
        .format('L')
        .replace(/\//g, separator),
    [locale]
  );

  const formatDateCustom = useCallback(
    (date: string | Date, format = 'L'): string =>
      dayjs(date).locale(locale).tz('Europe/Brussels').format(format),
    [locale]
  );

  // current date in ISO8601, without fraction seconds e.g. '2020-04-02T08:02:17-05:00'
  const formatDateIso = useCallback(
    (date: string | Date): string =>
      dayjs(date).locale(locale).tz('Europe/Brussels').format(),
    [locale]
  );

  const formatDateWithoutYear = useCallback(
    (date: string | Date, separator: string = '.'): string =>
      formatDate(date)
        .replace(/(\.|-)\d{4}/, '')
        .replace(/\./g, separator),
    [formatDate]
  );

  useEffect(() => {
    // set local globally
    dayjs.locale(locale);
  }, [locale]);

  return {
    formatDate,
    formatDateWithoutYear,
    formatDateIso,
    formatDateCustom,
    dayjs,
  };
};
